export interface ClassData {
  id: string;
  class_name: string;
  sections: string[];
}

export interface StudentData {
  id: number;
  user_id: number;
  name: string;
  gender: string;
  email_id: string;
  class_id: string;
  section: string | null;
  admission_no: string;
  roll_no: number;
  dob: string;
  blood_group: string;
  father_name: string;
  mother_name: string;
  father_no: string;
  mother_no: string;
  address: string;
  created_at: string;
  updated_at: string;
  user_email: string;
  profile_picture_url: string;
  class_name: string;
}

export interface GenderCounts {
  Male: number;
  Female: number;
  Other: number;
  total: number;
}

// ─── Shared barcode init script ───────────────────────────────────────────────
const BARCODE_SCRIPT = `
  <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
  <script>
    document.addEventListener('DOMContentLoaded', function () {
      document.querySelectorAll('svg.barcode').forEach(function(el) {
        var val = String(el.getAttribute('data-id') || '');
        JsBarcode(el, val, {
          format: 'CODE128',
          displayValue: false,
          fontSize: 6,
          textMargin: 0,
          height: 18,
          width: 0.9,
          margin: 1,
          background: 'transparent',
          lineColor: '#111827'
        });
      });
    });
  <\/script>
`;

// ─── Gender filter label helper ───────────────────────────────────────────────
function genderLabel(genderFilter?: string): string {
  if (genderFilter === 'Male') return ' (Male Students)';
  if (genderFilter === 'Female') return ' (Female Students)';
  return '';
}

// ─── Shared table rows renderer ───────────────────────────────────────────────
function renderTableRows(students: StudentData[]): string {
  return students.map((student) => `
    <tr>
      <td>
        ${student.profile_picture_url
          ? `<img src="${student.profile_picture_url}" alt="${student.name}" class="profile-pic"
                onerror="this.style.display='none';this.nextElementSibling.style.display='flex';" />
             <div class="profile-pic-placeholder" style="display:none">${student.name.charAt(0)}</div>`
          : `<div class="profile-pic-placeholder">${student.name.charAt(0)}</div>`
        }
      </td>
      <td><strong>${student.roll_no}</strong></td>
      <td class="student-name">${student.name}</td>
      <td>${student.gender}</td>
      <td>${new Date(student.dob).toLocaleDateString('en-GB')}</td>
      <td>${student.blood_group}</td>
      <td>${student.father_name}</td>
      <td>${student.mother_name}</td>
      <td>${student.father_no}<br/>${student.mother_no !== student.father_no ? student.mother_no : ''}</td>
      <td class="address-cell">${student.address}</td>
      <td>${student.admission_no}</td>
      <td class="barcode-cell">
        <svg class="barcode" data-id="${student.id}"></svg>
      </td>
    </tr>
  `).join('');
}

const TABLE_HEADER_ROW = `
  <tr>
    <th style="width:35px">Picture</th>
    <th style="width:30px">Roll</th>
    <th style="width:90px">Name</th>
    <th style="width:35px">Gender</th>
    <th style="width:55px">DOB</th>
    <th style="width:40px">Blood</th>
    <th style="width:75px">Father</th>
    <th style="width:75px">Mother</th>
    <th style="width:65px">Contact</th>
    <th style="width:90px">Address</th>
    <th style="width:55px">Admission No.</th>
    <th style="width:55px">Finder Id</th>
  </tr>
`;

// ─── TABLE REPORT ─────────────────────────────────────────────────────────────
export function generateTableReportHTML(
  students: StudentData[],
  classes: ClassData[],
  selectedClass: string | null,
  selectedSection: string | null,
  genderCounts: GenderCounts | null,
  genderFilter?: string
): string {
  const className = classes.find((c) => c.id === selectedClass)?.class_name || 'Unknown';
  const sectionText = selectedSection ? ` - Section ${selectedSection}` : '';
  const title = `Student Records - Class ${className}${sectionText}${genderLabel(genderFilter)}`;

  const accentColor = genderFilter === 'Female' ? '#be185d' : '#1e40af';
  const accentColorLight = genderFilter === 'Female' ? '#fdf2f8' : '#f8fafc';

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title}</title>
        ${BARCODE_SCRIPT}
        <style>
          @page { size: A4 landscape; margin: 0.3in; }
          @media print {
            body { margin: 0; font-family: Arial, sans-serif; font-size: 10px; color: #000; }
            .no-print { display: none !important; }
            table { page-break-inside: auto; page-break-before: avoid; }
            thead { display: table-header-group; }
            tbody tr { page-break-inside: avoid; page-break-after: auto; }
          }
          body { font-family: Arial, sans-serif; margin: 10px; background: #fff; line-height: 1.1; }
          .controls { margin-bottom: 10px; text-align: center; }
          .controls button {
            background: ${accentColor}; color: white; border: none;
            padding: 6px 12px; border-radius: 3px; cursor: pointer; font-size: 11px; margin: 0 5px;
          }
          .controls button.close-btn { background: #dc2626; }
          .header-section {
            text-align: center; margin-bottom: 10px;
            border-bottom: 2px solid ${accentColor}; padding-bottom: 5px;
          }
          .header-section h1 { color: ${accentColor}; margin: 0 0 3px 0; font-size: 16px; font-weight: bold; }
          .header-section .meta { color: #666; font-size: 9px; margin: 2px 0; }
          .gender-banner {
            display: inline-block; margin: 4px auto; padding: 3px 14px; border-radius: 20px;
            font-size: 10px; font-weight: 700;
            background: ${genderFilter === 'Female' ? '#fce7f3' : '#eff6ff'};
            color: ${genderFilter === 'Female' ? '#be185d' : '#1e40af'};
            border: 1px solid ${genderFilter === 'Female' ? '#fbcfe8' : '#bfdbfe'};
          }
          .stats { display: flex; justify-content: center; gap: 15px; margin: 5px 0 10px 0; }
          .stat-box {
            background: ${accentColorLight}; padding: 3px 8px; border-radius: 2px;
            border-left: 2px solid ${accentColor}; font-size: 9px;
          }
          .student-table { width: 100%; border-collapse: collapse; margin-top: 2px; font-size: 8px; }
          .student-table th {
            background: ${accentColor}; color: white; padding: 4px 2px;
            text-align: center; font-weight: bold; border: 1px solid #ddd; font-size: 8px;
          }
          .student-table td {
            padding: 3px 2px; border: 1px solid #ddd;
            text-align: center; vertical-align: middle; font-size: 7px;
          }
          .student-table tr:nth-child(even) { background: ${accentColorLight}; }
          .profile-pic { width: 24px; height: 24px; border-radius: 50%; object-fit: cover; border: 1px solid #e5e7eb; }
          .profile-pic-placeholder {
            width: 24px; height: 24px; border-radius: 50%; background: #e5e7eb;
            display: flex; align-items: center; justify-content: center;
            font-size: 8px; color: #6b7280; margin: 0 auto;
          }
          .student-name { font-weight: 600; color: #1f2937; font-size: 8px; }
          .address-cell { max-width: 70px; word-wrap: break-word; font-size: 7px; line-height: 1.0; }
          .barcode-cell svg { display: block; margin: 0 auto; }
          .footer { margin-top: 10px; text-align: center; color: #666; font-size: 8px; border-top: 1px solid #ddd; padding-top: 5px; }
        </style>
      </head>
      <body>
        <div class="no-print controls">
          <button onclick="window.print()">🖨️ Print / Save as PDF</button>
          <button class="close-btn" onclick="window.close()">❌ Close</button>
        </div>
        <div class="header-section">
          <h1>SKOOLPILLAR MANAGEMENT SYSTEM</h1>
          <div class="meta">${title}</div>
          ${genderFilter ? `<div><span class="gender-banner">${genderFilter === 'Male' ? '♂' : '♀'} ${genderFilter} Students Only</span></div>` : ''}
          <div class="meta">Generated: ${new Date().toLocaleDateString('en-GB')} ${new Date().toLocaleTimeString('en-GB')}</div>
          <div class="stats">
            <div class="stat-box"><strong>Total:</strong> ${students.length}</div>
            ${!genderFilter || genderFilter === 'Male' ? `<div class="stat-box"><strong>Male:</strong> ${genderCounts?.Male ?? students.filter(s => s.gender === 'Male').length}</div>` : ''}
            ${!genderFilter || genderFilter === 'Female' ? `<div class="stat-box"><strong>Female:</strong> ${genderCounts?.Female ?? students.filter(s => s.gender === 'Female').length}</div>` : ''}
            ${!genderFilter ? `<div class="stat-box"><strong>Other:</strong> ${genderCounts?.Other ?? students.filter(s => s.gender === 'Other').length}</div>` : ''}
          </div>
        </div>
        <table class="student-table">
          <thead>${TABLE_HEADER_ROW}</thead>
          <tbody>${renderTableRows(students)}</tbody>
        </table>
        <div class="footer">
          <p><strong>Skoolpillar Management System</strong> | ${students.length} student records generated${genderFilter ? ` (${genderFilter} only)` : ''}</p>
        </div>
      </body>
    </html>
  `;
}

// ─── VERIFICATION CARDS ───────────────────────────────────────────────────────
export function generateVerificationCardsHTML(
  students: StudentData[],
  classes: ClassData[],
  selectedClass: string | null,
  selectedSection: string | null,
  genderCounts: GenderCounts | null,
  genderFilter?: string
): string {
  const className = classes.find((c) => c.id === selectedClass)?.class_name || 'Unknown';
  const sectionText = selectedSection ? ` - Section ${selectedSection}` : '';
  const title = `Student Verification Cards - Class ${className}${sectionText}${genderLabel(genderFilter)}`;

  const headerGradient = genderFilter === 'Female'
    ? 'linear-gradient(135deg, #9d174d 0%, #ec4899 100%)'
    : 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)';
  const classBadgeBg = genderFilter === 'Female'
    ? 'linear-gradient(135deg, #be185d, #f472b6)'
    : 'linear-gradient(135deg, #1e40af, #3b82f6)';
  const rollBadgeBg     = genderFilter === 'Female' ? '#fdf2f8' : '#eff6ff';
  const rollBadgeBorder = genderFilter === 'Female' ? '#fbcfe8' : '#bfdbfe';
  const rollBadgeColor  = genderFilter === 'Female' ? '#be185d' : '#1e40af';
  const photoBorder     = genderFilter === 'Female' ? '#be185d' : '#1e40af';
  const statPillBg      = genderFilter === 'Female' ? '#fdf2f8' : '#eff6ff';
  const statPillColor   = genderFilter === 'Female' ? '#be185d' : '#1e40af';
  const statPillBorder  = genderFilter === 'Female' ? '#fbcfe8' : '#bfdbfe';
  const h1Color         = genderFilter === 'Female' ? '#9d174d' : '#1e40af';
  const reportBorderColor = genderFilter === 'Female' ? '#be185d' : '#1e40af';

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title}</title>
        ${BARCODE_SCRIPT}
        <style>
          @page { size: A4 portrait; margin: 0.4in; }
          @media print {
            body { margin: 0; }
            .no-print { display: none !important; }
            .cards-grid { gap: 12px; }
            .card { page-break-inside: avoid; }
          }
          * { box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; margin: 0; background: #fff; }
          .no-print {
            padding: 12px; text-align: center;
            background: #f8fafc; border-bottom: 1px solid #e5e7eb; margin-bottom: 16px;
          }
          .no-print button {
            background: ${h1Color}; color: white; border: none;
            padding: 8px 16px; border-radius: 6px; cursor: pointer; font-size: 12px; margin: 0 6px; font-weight: 600;
          }
          .no-print button.close-btn { background: #dc2626; }
          .report-header {
            text-align: center; padding: 0 0 14px 0;
            border-bottom: 3px solid ${reportBorderColor}; margin-bottom: 20px;
          }
          .report-header h1 { color: ${h1Color}; font-size: 18px; font-weight: 800; margin: 0 0 4px 0; letter-spacing: 0.5px; }
          .report-header .meta { color: #64748b; font-size: 10px; margin: 2px 0; }
          .gender-banner {
            display: inline-block; margin: 4px auto 6px auto; padding: 3px 16px;
            border-radius: 20px; font-size: 11px; font-weight: 700;
            background: ${statPillBg}; color: ${statPillColor}; border: 1px solid ${statPillBorder};
          }
          .report-header .stats { display: flex; justify-content: center; gap: 12px; margin-top: 8px; }
          .stat-pill {
            padding: 3px 10px; border-radius: 20px; font-size: 10px; font-weight: 700;
            background: ${statPillBg}; color: ${statPillColor}; border: 1px solid ${statPillBorder};
          }
          .cards-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
          .card { border: 1.5px solid #e2e8f0; border-radius: 10px; overflow: hidden; background: #fff; box-shadow: 0 1px 4px rgba(0,0,0,0.06); }
          .card-header { padding: 8px 10px 6px 10px; display: flex; align-items: center; gap: 8px; }
          .card-header .school-name { font-size: 7px; font-weight: 800; color: rgba(255,255,255,0.9); letter-spacing: 0.3px; text-transform: uppercase; }
          .card-header .card-label { font-size: 6px; color: rgba(255,255,255,0.65); font-weight: 500; }
          .card-header-logo {
            width: 24px; height: 24px; border-radius: 5px; background: rgba(255,255,255,0.2);
            display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 12px;
          }
          .card-body { padding: 10px; display: flex; gap: 10px; align-items: flex-start; }
          .photo-wrapper { flex-shrink: 0; }
          .student-photo { width: 70px; height: 80px; object-fit: cover; border-radius: 7px; border: 2px solid ${photoBorder}; display: block; }
          .student-photo-placeholder {
            width: 70px; height: 80px; border-radius: 7px; background: ${headerGradient};
            display: flex; align-items: center; justify-content: center;
            font-size: 28px; font-weight: 700; color: white; border: 2px solid ${photoBorder};
          }
          .roll-badge {
            margin-top: 5px; text-align: center; background: ${rollBadgeBg};
            border: 1px solid ${rollBadgeBorder}; border-radius: 4px; padding: 2px 4px;
            font-size: 8px; color: ${rollBadgeColor}; font-weight: 700;
          }
          .student-info { flex: 1; min-width: 0; }
          .student-name-label { font-size: 12px; font-weight: 800; color: #111827; line-height: 1.2; margin-bottom: 4px; word-break: break-word; }
          .class-badge { display: inline-block; padding: 2px 7px; background: ${classBadgeBg}; color: white; border-radius: 4px; font-size: 8px; font-weight: 700; margin-bottom: 7px; }
          .info-table { width: 100%; border-collapse: collapse; }
          .info-table td { font-size: 9.8px; padding: 2px 0; color: #374151; vertical-align: top; line-height: 1.4; }
          .info-table td.label { font-weight: 700; color: #6b7280; width: 44%; white-space: nowrap; font-size: 9.5px; }
          .info-table td.value { color: #111827; }
          .card-barcode { border-top: 1px dashed #e2e8f0; padding: 6px 10px 4px 10px; display: flex; align-items: center; justify-content: center; background: #fafafa; }
          .card-barcode svg { display: block; }
          .card-footer { background: #f8fafc; border-top: 1px solid #e5e7eb; padding: 5px 10px; display: flex; align-items: center; justify-content: space-between; }
          .admission-no { font-size: 7px; color: #6b7280; font-weight: 600; }
          .blood-badge { font-size: 8px; font-weight: 800; color: #dc2626; background: #fef2f2; border: 1px solid #fecaca; padding: 1px 6px; border-radius: 4px; }
          .gender-dot { display: inline-block; width: 7px; height: 7px; border-radius: 50%; margin-right: 3px; vertical-align: middle; }
          .gender-male { background: #3b82f6; }
          .gender-female { background: #ec4899; }
          .gender-other { background: #a855f7; }
          .footer { margin-top: 20px; text-align: center; color: #94a3b8; font-size: 9px; border-top: 1px solid #e5e7eb; padding-top: 10px; }
        </style>
      </head>
      <body>
        <div class="no-print">
          <button onclick="window.print()">🖨️ Print / Save as PDF</button>
          <button class="close-btn" onclick="window.close()">❌ Close</button>
        </div>
        <div class="report-header">
          <h1>SKOOLPILLAR MANAGEMENT SYSTEM</h1>
          <div class="meta">${title}</div>
          ${genderFilter ? `<div><span class="gender-banner">${genderFilter === 'Male' ? '♂' : '♀'} ${genderFilter} Students Only</span></div>` : ''}
          <div class="meta">Generated: ${new Date().toLocaleDateString('en-GB')} ${new Date().toLocaleTimeString('en-GB')}</div>
          <div class="stats">
            <div class="stat-pill">Total: ${students.length}</div>
            ${!genderFilter || genderFilter === 'Male' ? `<div class="stat-pill">♂ ${genderCounts?.Male ?? students.filter(s => s.gender === 'Male').length}</div>` : ''}
            ${!genderFilter || genderFilter === 'Female' ? `<div class="stat-pill">♀ ${genderCounts?.Female ?? students.filter(s => s.gender === 'Female').length}</div>` : ''}
            ${!genderFilter && (genderCounts?.Other ?? students.filter(s => s.gender === 'Other').length) > 0
              ? `<div class="stat-pill">⚥ ${genderCounts?.Other ?? students.filter(s => s.gender === 'Other').length}</div>`
              : ''}
          </div>
        </div>
        <div class="cards-grid">
          ${students.map((student) => `
            <div class="card">
              <div class="card-header">
                <div class="card-header-logo">🏫</div>
                <div>
                  <div class="school-name">Skoolpillar</div>
                  <div class="card-label">Student Verification Card</div>
                </div>
              </div>
              <div class="card-body">
                <div class="photo-wrapper">
                  ${student.profile_picture_url
                    ? `<img src="${student.profile_picture_url}" alt="${student.name}"
                          class="student-photo"
                          onerror="this.style.display='none';this.nextElementSibling.style.display='flex';" />
                       <div class="student-photo-placeholder" style="display:none">${student.name.charAt(0)}</div>`
                    : `<div class="student-photo-placeholder">${student.name.charAt(0)}</div>`
                  }
                  <div class="roll-badge">Roll: ${student.roll_no}</div>
                </div>
                <div class="student-info">
                  <div class="student-name-label">${student.name}</div>
                  <div class="class-badge">${student.class_name}${student.section ? ' – ' + student.section : ''}</div>
                  <table class="info-table">
                    <tr>
                      <td class="label">Gender</td>
                      <td class="value"><span class="gender-dot gender-${student.gender.toLowerCase()}"></span>${student.gender}</td>
                    </tr>
                    <tr><td class="label">DOB</td><td class="value">${new Date(student.dob).toLocaleDateString('en-GB')}</td></tr>
                    <tr><td class="label">Father</td><td class="value">${student.father_name}</td></tr>
                    <tr><td class="label">Mother</td><td class="value">${student.mother_name}</td></tr>
                    <tr><td class="label">Father No.</td><td class="value">${student.father_no}</td></tr>
                    ${student.mother_no && student.mother_no !== student.father_no
                      ? `<tr><td class="label">Mother No.</td><td class="value">${student.mother_no}</td></tr>` : ''}
                    <tr><td class="label">Address</td><td class="value">${student.address}</td></tr>
                  </table>
                </div>
              </div>
              <div class="card-barcode"><svg class="barcode" data-id="${student.id}"></svg></div>
              <div class="card-footer">
                <span class="admission-no">Adm# ${student.admission_no}</span>
                <span class="blood-badge">🩸 ${student.blood_group}</span>
              </div>
            </div>
          `).join('')}
        </div>
        <div class="footer">
          <p><strong>Skoolpillar Management System</strong> | ${students.length} verification cards generated${genderFilter ? ` (${genderFilter} only)` : ''}</p>
        </div>
      </body>
    </html>
  `;
}

// ─── SPLIT TABLE REPORT (Male section + Female section in one PDF) ─────────────
export function generateSplitTableReportHTML(
  students: StudentData[],
  classes: ClassData[],
  selectedClass: string | null,
  selectedSection: string | null,
  genderCounts: GenderCounts | null
): string {
  const className = classes.find((c) => c.id === selectedClass)?.class_name || 'Unknown';
  const sectionText = selectedSection ? ` - Section ${selectedSection}` : '';
  const baseTitle = `Student Records - Class ${className}${sectionText}`;
  const generatedAt = `${new Date().toLocaleDateString('en-GB')} ${new Date().toLocaleTimeString('en-GB')}`;

  const maleStudents   = students.filter(s => s.gender === 'Male');
  const femaleStudents = students.filter(s => s.gender === 'Female');

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${baseTitle} (Split by Gender)</title>
        ${BARCODE_SCRIPT}
        <style>
          @page { size: A4 landscape; margin: 0.3in; }
          @media print {
            body { margin: 0; font-family: Arial, sans-serif; font-size: 10px; color: #000; }
            .no-print { display: none !important; }
            table { page-break-inside: auto; }
            thead { display: table-header-group; }
            tbody tr { page-break-inside: avoid; page-break-after: auto; }
            .female-section { page-break-before: always; }
          }
          body { font-family: Arial, sans-serif; margin: 10px; background: #fff; line-height: 1.1; }
          .controls { margin-bottom: 10px; text-align: center; }
          .controls button {
            background: #1e40af; color: white; border: none;
            padding: 6px 12px; border-radius: 3px; cursor: pointer; font-size: 11px; margin: 0 5px;
          }
          .controls button.close-btn { background: #dc2626; }
          .header-section {
            text-align: center; margin-bottom: 10px; padding-bottom: 5px;
            border-bottom: 2px solid;
          }
          .header-section h1 { margin: 0 0 3px 0; font-size: 16px; font-weight: bold; }
          .header-section .meta { color: #666; font-size: 9px; margin: 2px 0; }
          .gender-banner {
            display: inline-block; margin: 4px auto; padding: 3px 14px;
            border-radius: 20px; font-size: 10px; font-weight: 700; border: 1px solid;
          }
          .stats { display: flex; justify-content: center; gap: 15px; margin: 5px 0 10px 0; }
          .stat-box { padding: 3px 8px; border-radius: 2px; border-left: 2px solid; font-size: 9px; }
          .student-table { width: 100%; border-collapse: collapse; font-size: 8px; }
          .student-table td { padding: 3px 2px; border: 1px solid #ddd; text-align: center; vertical-align: middle; font-size: 7px; }
          .male-table thead th { background: #1e40af; color: white; padding: 4px 2px; text-align: center; font-weight: bold; border: 1px solid #ddd; font-size: 8px; }
          .female-table thead th { background: #be185d; color: white; padding: 4px 2px; text-align: center; font-weight: bold; border: 1px solid #ddd; font-size: 8px; }
          .male-table tr:nth-child(even) { background: #eff6ff; }
          .female-table tr:nth-child(even) { background: #fdf2f8; }
          .profile-pic { width: 24px; height: 24px; border-radius: 50%; object-fit: cover; border: 1px solid #e5e7eb; }
          .profile-pic-placeholder {
            width: 24px; height: 24px; border-radius: 50%; background: #e5e7eb;
            display: flex; align-items: center; justify-content: center;
            font-size: 8px; color: #6b7280; margin: 0 auto;
          }
          .student-name { font-weight: 600; color: #1f2937; font-size: 8px; }
          .address-cell { max-width: 70px; word-wrap: break-word; font-size: 7px; line-height: 1.0; }
          .barcode-cell svg { display: block; margin: 0 auto; }
          .female-section { margin-top: 24px; }
          .footer { margin-top: 10px; text-align: center; color: #666; font-size: 8px; border-top: 1px solid #ddd; padding-top: 5px; }
        </style>
      </head>
      <body>
        <div class="no-print controls">
          <button onclick="window.print()">🖨️ Print / Save as PDF</button>
          <button class="close-btn" onclick="window.close()">❌ Close</button>
        </div>

        ${maleStudents.length > 0 ? `
          <div class="header-section" style="border-bottom-color:#1e40af;">
            <h1 style="color:#1e40af;">SKOOLPILLAR MANAGEMENT SYSTEM</h1>
            <div class="meta">${baseTitle} (Male Students)</div>
            <div><span class="gender-banner" style="background:#eff6ff;color:#1e40af;border-color:#bfdbfe;">♂ Male Students Only</span></div>
            <div class="meta">Generated: ${generatedAt}</div>
            <div class="stats">
              <div class="stat-box" style="background:#eff6ff;border-left-color:#1e40af;"><strong>Total:</strong> ${maleStudents.length}</div>
              <div class="stat-box" style="background:#eff6ff;border-left-color:#1e40af;"><strong>Male:</strong> ${maleStudents.length}</div>
            </div>
          </div>
          <table class="student-table male-table">
            <thead>${TABLE_HEADER_ROW}</thead>
            <tbody>${renderTableRows(maleStudents)}</tbody>
          </table>
        ` : ''}

        ${femaleStudents.length > 0 ? `
          <div class="female-section">
            <div class="header-section" style="border-bottom-color:#be185d;">
              <h1 style="color:#be185d;">SKOOLPILLAR MANAGEMENT SYSTEM</h1>
              <div class="meta">${baseTitle} (Female Students)</div>
              <div><span class="gender-banner" style="background:#fdf2f8;color:#be185d;border-color:#fbcfe8;">♀ Female Students Only</span></div>
              <div class="meta">Generated: ${generatedAt}</div>
              <div class="stats">
                <div class="stat-box" style="background:#fdf2f8;border-left-color:#be185d;"><strong>Total:</strong> ${femaleStudents.length}</div>
                <div class="stat-box" style="background:#fdf2f8;border-left-color:#be185d;"><strong>Female:</strong> ${femaleStudents.length}</div>
              </div>
            </div>
            <table class="student-table female-table">
              <thead>${TABLE_HEADER_ROW}</thead>
              <tbody>${renderTableRows(femaleStudents)}</tbody>
            </table>
          </div>
        ` : ''}

        <div class="footer">
          <p><strong>Skoolpillar Management System</strong> | ${students.length} records | ♂ ${maleStudents.length} Male · ♀ ${femaleStudents.length} Female</p>
        </div>
      </body>
    </html>
  `;
}

// ─── SPLIT VERIFICATION CARDS (Male section + Female section in one PDF) ───────
export function generateSplitVerificationCardsHTML(
  students: StudentData[],
  classes: ClassData[],
  selectedClass: string | null,
  selectedSection: string | null,
  genderCounts: GenderCounts | null
): string {
  const className = classes.find((c) => c.id === selectedClass)?.class_name || 'Unknown';
  const sectionText = selectedSection ? ` - Section ${selectedSection}` : '';
  const baseTitle = `Student Verification Cards - Class ${className}${sectionText}`;
  const generatedAt = `${new Date().toLocaleDateString('en-GB')} ${new Date().toLocaleTimeString('en-GB')}`;

  const maleStudents   = students.filter(s => s.gender === 'Male');
  const femaleStudents = students.filter(s => s.gender === 'Female');

  const renderCards = (
    list: StudentData[],
    headerGradient: string,
    classBadgeBg: string,
    rollBadgeBg: string,
    rollBadgeBorder: string,
    rollBadgeColor: string,
    photoBorder: string
  ) => list.map((student) => `
    <div class="card">
      <div class="card-header" style="background:${headerGradient};">
        <div class="card-header-logo">🏫</div>
        <div>
          <div class="school-name">Skoolpillar</div>
          <div class="card-label">Student Verification Card</div>
        </div>
      </div>
      <div class="card-body">
        <div class="photo-wrapper">
          ${student.profile_picture_url
            ? `<img src="${student.profile_picture_url}" alt="${student.name}"
                  class="student-photo" style="border-color:${photoBorder};"
                  onerror="this.style.display='none';this.nextElementSibling.style.display='flex';" />
               <div class="student-photo-placeholder" style="background:${headerGradient};border-color:${photoBorder};display:none;">${student.name.charAt(0)}</div>`
            : `<div class="student-photo-placeholder" style="background:${headerGradient};border-color:${photoBorder};">${student.name.charAt(0)}</div>`
          }
          <div class="roll-badge" style="background:${rollBadgeBg};border-color:${rollBadgeBorder};color:${rollBadgeColor};">
            Roll: ${student.roll_no}
          </div>
        </div>
        <div class="student-info">
          <div class="student-name-label">${student.name}</div>
          <div class="class-badge" style="background:${classBadgeBg};">
            ${student.class_name}${student.section ? ' – ' + student.section : ''}
          </div>
          <table class="info-table">
            <tr><td class="label">Gender</td><td class="value">${student.gender}</td></tr>
            <tr><td class="label">DOB</td><td class="value">${new Date(student.dob).toLocaleDateString('en-GB')}</td></tr>
            <tr><td class="label">Father</td><td class="value">${student.father_name}</td></tr>
            <tr><td class="label">Mother</td><td class="value">${student.mother_name}</td></tr>
            <tr><td class="label">Father No.</td><td class="value">${student.father_no}</td></tr>
            ${student.mother_no && student.mother_no !== student.father_no
              ? `<tr><td class="label">Mother No.</td><td class="value">${student.mother_no}</td></tr>`
              : ''}
            <tr><td class="label">Address</td><td class="value">${student.address}</td></tr>
          </table>
        </div>
      </div>
      <div class="card-barcode"><svg class="barcode" data-id="${student.id}"></svg></div>
      <div class="card-footer">
        <span class="admission-no">Adm# ${student.admission_no}</span>
        <span class="blood-badge">🩸 ${student.blood_group}</span>
      </div>
    </div>
  `).join('');

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${baseTitle} (Split by Gender)</title>
        ${BARCODE_SCRIPT}
        <style>
          @page { size: A4 portrait; margin: 0.4in; }
          @media print {
            body { margin: 0; }
            .no-print { display: none !important; }
            .card { page-break-inside: avoid; }
            .female-section { page-break-before: always; }
          }
          * { box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; margin: 0; background: #fff; }
          .no-print {
            padding: 12px; text-align: center;
            background: #f8fafc; border-bottom: 1px solid #e5e7eb; margin-bottom: 16px;
          }
          .no-print button {
            background: #1e40af; color: white; border: none;
            padding: 8px 16px; border-radius: 6px; cursor: pointer; font-size: 12px; margin: 0 6px; font-weight: 600;
          }
          .no-print button.close-btn { background: #dc2626; }
          .report-header {
            text-align: center; padding: 0 0 14px 0;
            border-bottom: 3px solid; margin-bottom: 20px;
          }
          .report-header h1 { font-size: 18px; font-weight: 800; margin: 0 0 4px 0; letter-spacing: 0.5px; }
          .report-header .meta { color: #64748b; font-size: 10px; margin: 2px 0; }
          .gender-banner {
            display: inline-block; margin: 4px auto 6px auto; padding: 3px 16px;
            border-radius: 20px; font-size: 11px; font-weight: 700; border: 1px solid;
          }
          .report-header .stats { display: flex; justify-content: center; gap: 12px; margin-top: 8px; }
          .stat-pill { padding: 3px 10px; border-radius: 20px; font-size: 10px; font-weight: 700; border: 1px solid; }
          .cards-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
          .card { border: 1.5px solid #e2e8f0; border-radius: 10px; overflow: hidden; background: #fff; box-shadow: 0 1px 4px rgba(0,0,0,0.06); }
          .card-header { padding: 8px 10px 6px 10px; display: flex; align-items: center; gap: 8px; }
          .card-header .school-name { font-size: 7px; font-weight: 800; color: rgba(255,255,255,0.9); letter-spacing: 0.3px; text-transform: uppercase; }
          .card-header .card-label { font-size: 6px; color: rgba(255,255,255,0.65); font-weight: 500; }
          .card-header-logo {
            width: 24px; height: 24px; border-radius: 5px; background: rgba(255,255,255,0.2);
            display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 12px;
          }
          .card-body { padding: 10px; display: flex; gap: 10px; align-items: flex-start; }
          .photo-wrapper { flex-shrink: 0; }
          .student-photo { width: 70px; height: 80px; object-fit: cover; border-radius: 7px; border: 2px solid; display: block; }
          .student-photo-placeholder { width: 70px; height: 80px; border-radius: 7px; display: flex; align-items: center; justify-content: center; font-size: 28px; font-weight: 700; color: white; border: 2px solid; }
          .roll-badge { margin-top: 5px; text-align: center; border-radius: 4px; padding: 2px 4px; font-size: 8px; font-weight: 700; border: 1px solid; }
          .student-info { flex: 1; min-width: 0; }
          .student-name-label { font-size: 12px; font-weight: 800; color: #111827; line-height: 1.2; margin-bottom: 4px; word-break: break-word; }
          .class-badge { display: inline-block; padding: 2px 7px; color: white; border-radius: 4px; font-size: 8px; font-weight: 700; margin-bottom: 7px; }
          .info-table { width: 100%; border-collapse: collapse; }
          .info-table td { font-size: 9.8px; padding: 2px 0; color: #374151; vertical-align: top; line-height: 1.4; }
          .info-table td.label { font-weight: 700; color: #6b7280; width: 44%; white-space: nowrap; font-size: 9.5px; }
          .info-table td.value { color: #111827; }
          .card-barcode { border-top: 1px dashed #e2e8f0; padding: 6px 10px 4px 10px; display: flex; align-items: center; justify-content: center; background: #fafafa; }
          .card-barcode svg { display: block; }
          .card-footer { background: #f8fafc; border-top: 1px solid #e5e7eb; padding: 5px 10px; display: flex; align-items: center; justify-content: space-between; }
          .admission-no { font-size: 7px; color: #6b7280; font-weight: 600; }
          .blood-badge { font-size: 8px; font-weight: 800; color: #dc2626; background: #fef2f2; border: 1px solid #fecaca; padding: 1px 6px; border-radius: 4px; }
          .female-section { margin-top: 30px; }
          .footer { margin-top: 20px; text-align: center; color: #94a3b8; font-size: 9px; border-top: 1px solid #e5e7eb; padding-top: 10px; }
        </style>
      </head>
      <body>
        <div class="no-print">
          <button onclick="window.print()">🖨️ Print / Save as PDF</button>
          <button class="close-btn" onclick="window.close()">❌ Close</button>
        </div>

        ${maleStudents.length > 0 ? `
          <div class="report-header" style="border-bottom-color:#1e40af;">
            <h1 style="color:#1e40af;">SKOOLPILLAR MANAGEMENT SYSTEM</h1>
            <div class="meta">${baseTitle} (Male Students)</div>
            <div><span class="gender-banner" style="background:#eff6ff;color:#1e40af;border-color:#bfdbfe;">♂ Male Students Only</span></div>
            <div class="meta">Generated: ${generatedAt}</div>
            <div class="stats">
              <div class="stat-pill" style="background:#eff6ff;color:#1e40af;border-color:#bfdbfe;">Total: ${maleStudents.length}</div>
              <div class="stat-pill" style="background:#eff6ff;color:#1e40af;border-color:#bfdbfe;">♂ ${maleStudents.length}</div>
            </div>
          </div>
          <div class="cards-grid">
            ${renderCards(
              maleStudents,
              'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
              'linear-gradient(135deg, #1e40af, #3b82f6)',
              '#eff6ff', '#bfdbfe', '#1e40af', '#1e40af'
            )}
          </div>
        ` : ''}

        ${femaleStudents.length > 0 ? `
          <div class="female-section">
            <div class="report-header" style="border-bottom-color:#be185d;">
              <h1 style="color:#be185d;">SKOOLPILLAR MANAGEMENT SYSTEM</h1>
              <div class="meta">${baseTitle} (Female Students)</div>
              <div><span class="gender-banner" style="background:#fdf2f8;color:#be185d;border-color:#fbcfe8;">♀ Female Students Only</span></div>
              <div class="meta">Generated: ${generatedAt}</div>
              <div class="stats">
                <div class="stat-pill" style="background:#fdf2f8;color:#be185d;border-color:#fbcfe8;">Total: ${femaleStudents.length}</div>
                <div class="stat-pill" style="background:#fdf2f8;color:#be185d;border-color:#fbcfe8;">♀ ${femaleStudents.length}</div>
              </div>
            </div>
            <div class="cards-grid">
              ${renderCards(
                femaleStudents,
                'linear-gradient(135deg, #9d174d 0%, #ec4899 100%)',
                'linear-gradient(135deg, #be185d, #f472b6)',
                '#fdf2f8', '#fbcfe8', '#be185d', '#be185d'
              )}
            </div>
          </div>
        ` : ''}

        <div class="footer">
          <p><strong>Skoolpillar Management System</strong> | ${students.length} cards | ♂ ${maleStudents.length} Male · ♀ ${femaleStudents.length} Female</p>
        </div>
      </body>
    </html>
  `;
}